import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Header from '../components/Header';
import MobileActionBar from '../components/MobileActionBar';
import {
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  ArrowRight,
  Clock,
  Calendar,
  Shield,
  Building2,
  TrendingDown,
  ChevronRight
} from 'lucide-react';
import { companyInfo } from '../data/mock';
const PlannedMaintenance = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', properties: '', message: '', honeypot: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.honeypot) return;
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          properties_managed: formData.properties,
          job_description: `[Planned Maintenance Enquiry] ${formData.message}`,
          trade_type: 'planned',
          source_website: 'expert-propertymaintenance.co.uk',
          source_page: '/planned-property-maintenance'
        }),
      });
      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', properties: '', message: '', honeypot: '' });
      } else {
        setSubmitError(true);
      }
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  const maintenanceTypes = [
    { title: 'Periodic Decorating', desc: 'Scheduled repainting of communal areas, individual units, or full properties to maintain appearance and protect surfaces' },
    { title: 'Annual Repair Lists', desc: 'Working through accumulated minor repairs across your portfolio in a coordinated, cost-effective way' },
    { title: 'Preventative Maintenance', desc: 'Addressing issues before they become expensive problems — silicone seals, grout, fixings, minor repairs' },
    { title: 'Joinery Maintenance', desc: 'Doors, skirting, architraves — keeping woodwork in good condition before replacement becomes necessary' },
    { title: 'Sealant & Grout Maintenance', desc: 'Bathrooms and kitchens require regular attention to prevent water damage and deterioration' },
    { title: 'Minor Plaster Repairs', desc: 'Addressing cracks, damage and wear before properties need major replastering' }
  ];
  const benefits = [
    'Prevent expensive emergency repairs',
    'Maintain property condition and value',
    'Keep tenants satisfied with well-maintained homes',
    'Budget more effectively with planned works',
    'Reduce void periods by addressing issues proactively',
    'Single contractor for multiple trades'
  ];
  const idealFor = [
    { title: 'Portfolio Landlords', desc: 'Managing multiple properties and wanting consistent maintenance across your portfolio' },
    { title: 'Letting Agents', desc: 'Responsible for maintenance on behalf of landlord clients' },
    { title: 'Property Management Companies', desc: 'Overseeing larger residential portfolios' },
    { title: 'Housing Associations', desc: 'Requiring planned works programmes for housing stock' }
  ];
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Planned Property Maintenance for Landlords & Agents | Expert Property Maintenance</title>
        <meta name="description" content="Planned property maintenance in Manchester for landlords and letting agents. Scheduled repairs, periodic decorating, preventative maintenance for rental properties. Call 07300 405045." />
        <link rel="canonical" href="https://expert-propertymaintenance.co.uk/planned-property-maintenance" />
      </Helmet>
      <Header variant="default" companyPhone={companyInfo.phone} />
      {/* Hero */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-20 bg-gradient-to-b from-brand-navy to-brand-navy/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-brand-yellow/10 border border-brand-yellow/30 text-brand-yellow px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Calendar className="w-4 h-4" />
              Property Maintenance
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Planned Property Maintenance
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Scheduled maintenance programmes to keep your rental properties in excellent condition. 
              Prevent costly emergency repairs and maintain tenant satisfaction through proactive property care.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold" asChild>
                <a href="#contact">
                  Discuss Your Portfolio
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-brand-navy" asChild>
                <a href={`tel:${companyInfo.phone}`}>
                  <Phone className="w-5 h-5 mr-2" />
                  {companyInfo.phone}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
      {/* What is Planned Maintenance */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-brand-navy mb-6">
                Why Planned Maintenance Matters
              </h2>
              <p className="text-lg text-brand-slate mb-6">
                Properties deteriorate over time. Paint fades, silicone fails, doors stick, plaster cracks. 
                Left unaddressed, minor issues become major problems — and emergency repairs always cost more 
                than planned works.
              </p>
              <p className="text-brand-slate mb-6">
                Planned maintenance lets you stay ahead of deterioration. Rather than waiting for tenants to 
                report problems or discovering issues during inspections, you schedule maintenance at convenient 
                times and budget accordingly.
              </p>
              <p className="text-brand-slate mb-8">
                Expert Property Maintenance works with landlords and agents to develop practical maintenance 
                programmes — whether that's annual decorating cycles, periodic repair lists, or ongoing 
                maintenance support across your portfolio.
              </p>
              <div className="space-y-3">
                {benefits.map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" />
                    <span className="text-brand-slate">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-6">
              <div className="bg-brand-navy text-white rounded-2xl p-8">
                <TrendingDown className="w-12 h-12 text-brand-yellow mb-4" />
                <h3 className="text-xl font-bold mb-4">Prevention vs Emergency</h3>
                <p className="text-slate-300 mb-4">
                  A failed silicone seal costs a few pounds to replace during planned maintenance. 
                  Left until water damage occurs, you're looking at plaster repairs, decorating, 
                  and potentially flooring replacement.
                </p>
                <p className="text-slate-400 text-sm">
                  Planned maintenance is an investment in your property's condition and your budget's predictability.
                </p>
              </div>
              <div className="bg-brand-grey border border-brand-yellow/30 rounded-2xl p-6">
                <h4 className="font-semibold text-brand-navy mb-4">Ideal For</h4>
                <div className="space-y-3">
                  {idealFor.map((item) => (
                    <div key={item.title} className="flex items-start gap-3">
                      <Building2 className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-medium text-brand-navy">{item.title}</span>
                        <p className="text-sm text-brand-slate">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Maintenance Types */}
      <section className="py-16 bg-brand-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-brand-navy mb-4 text-center">
            Types of Planned Maintenance
          </h2>
          <p className="text-lg text-brand-slate text-center mb-12 max-w-2xl mx-auto">
            Works that benefit from scheduling rather than reactive response.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {maintenanceTypes.map((type) => (
              <Card key={type.title} className="border-slate-200 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-brand-navy mb-2">{type.title}</h3>
                  <p className="text-brand-slate text-sm">{type.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-brand-navy mb-12 text-center">
            How Planned Maintenance Works
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: 1, title: 'Discuss Your Portfolio', desc: 'Tell us about your properties, their condition, and your maintenance priorities' },
              { step: 2, title: 'Inspection & Assessment', desc: 'We assess what needs attention now and what can be scheduled for later' },
              { step: 3, title: 'Plan & Schedule', desc: 'We agree a practical schedule that works around tenancies and your budget' },
              { step: 4, title: 'Deliver & Report', desc: 'Works completed to schedule with photos and completion reports' }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-14 h-14 bg-brand-yellow text-brand-navy rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold text-brand-navy mb-2">{item.title}</h3>
                <p className="text-brand-slate text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Related Services */}
      <section className="py-16 bg-brand-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-brand-navy mb-8">Related Services</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <a href="/reactive-property-maintenance" className="p-4 bg-white rounded-xl hover:bg-brand-grey border border-slate-200 transition-colors">
              <h4 className="font-semibold text-brand-navy">Reactive Maintenance</h4>
              <p className="text-sm text-brand-slate">Urgent repair response</p>
            </a>
            <a href="/painting-decorating" className="p-4 bg-white rounded-xl hover:bg-brand-grey border border-slate-200 transition-colors">
              <h4 className="font-semibold text-brand-navy">Painting & Decorating</h4>
              <p className="text-sm text-brand-slate">Professional decorating</p>
            </a>
            <a href="/letting-agent-maintenance" className="p-4 bg-white rounded-xl hover:bg-brand-grey border border-slate-200 transition-colors">
              <h4 className="font-semibold text-brand-navy">Letting Agent Services</h4>
              <p className="text-sm text-brand-slate">Agent contractor support</p>
            </a>
            <a href="/landlord-services" className="p-4 bg-white rounded-xl hover:bg-brand-grey border border-slate-200 transition-colors">
              <h4 className="font-semibold text-brand-navy">Landlord Services</h4>
              <p className="text-sm text-brand-slate">Complete landlord support</p>
            </a>
          </div>
        </div>
      </section>
      {/* Contact Form */}
      <section id="contact" className="py-16 bg-brand-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-6">Discuss Your Maintenance Needs</h2>
              <p className="text-slate-300 text-lg mb-8">
                Tell us about your portfolio and we will discuss how planned maintenance can work for you.
              </p>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center">
                    <Phone className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm">Call us</div>
                    <a href={`tel:${companyInfo.phone}`} className="text-white font-bold text-lg hover:text-brand-yellow">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center">
                    <Mail className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm">Email us</div>
                    <a href={`mailto:${companyInfo.email}`} className="text-white font-bold hover:text-brand-yellow">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
              </div>
              <div className="mt-8 p-4 bg-slate-800 rounded-xl">
                <div className="text-sm text-slate-400 mb-2">Insurance & Guarantee</div>
                <div className="text-white font-medium">{companyInfo.insurance.publicLiability}</div>
                <div className="text-white font-medium">{companyInfo.guarantee}</div>
              </div>
            </div>
            <Card className="bg-white border-0">
              <CardContent className="p-6 sm:p-8">
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-brand-navy mb-2">Enquiry Received</h3>
                    <p className="text-brand-slate">We will be in touch to discuss your portfolio.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <h3 className="text-xl font-bold text-brand-navy mb-4">Get in Touch</h3>
                    <input type="text" name="website" value={formData.honeypot} onChange={(e) => setFormData({...formData, honeypot: e.target.value})} style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">Your Name *</label>
                      <Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="John Smith" className="h-12" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-brand-slate mb-1.5">Email *</label>
                        <Input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="john@email.com" className="h-12" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-brand-slate mb-1.5">Phone *</label>
                        <Input type="tel" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="07xxx xxxxxx" className="h-12" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">Number of Properties (approx)</label>
                      <Input value={formData.properties} onChange={(e) => setFormData({...formData, properties: e.target.value})} placeholder="e.g., 10 properties in Stockport" className="h-12" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">Tell us about your maintenance needs *</label>
                      <Textarea required value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder="What maintenance would you like to discuss?" rows={4} />
                    </div>
                    <Button type="submit" className="w-full bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold h-12 text-lg" disabled={isSubmitting}>
                      {isSubmitting ? 'Sending...' : 'Send Enquiry'}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p>© {new Date().getFullYear()} Expert Property Maintenance</p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a href="/" className="hover:text-brand-yellow">Home</a>
              <a href="/landlord-services" className="hover:text-brand-yellow">Landlord Services</a>
              <a href="/letting-agent-maintenance" className="hover:text-brand-yellow">Letting Agents</a>
              <a href="/#contact" className="hover:text-brand-yellow">Contact</a>
            </div>
          </div>
        </div>
      </footer>
      <MobileActionBar phone={companyInfo.phone} />
    </div>
  );
};
export default PlannedMaintenance;
